import React from "react";
import { LayoutTemplate, Milestone } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@pgs-core/ui/components/dialog";
import type { SectionType } from "../lib/editor-content";

const OPTIONS: Array<{
  type: SectionType;
  label: string;
  description: string;
  Icon: typeof LayoutTemplate;
}> = [
  {
    type: "blank",
    label: "Blank",
    description: "Free-form content in up to three columns.",
    Icon: LayoutTemplate,
  },
  {
    type: "timeline",
    label: "Timeline",
    description: "A step-by-step process with numbered steps.",
    Icon: Milestone,
  },
];

/**
 * Picker shown by "Add section": choose what kind of section to add. The type
 * is fixed once picked — a timeline can't become a blank section later (and
 * vice versa), so the choice is made up front.
 */
export function SectionTypeDialog({ onPick, onCancel }: {
  onPick: (type: SectionType) => void;
  onCancel: () => void;
}) {
  return (
    <Dialog open onOpenChange={(open: boolean) => { if (!open) onCancel(); }}>
      {/* aria-describedby: the option buttons describe themselves (Radix warns otherwise). */}
      <DialogContent className="max-w-md" aria-describedby={undefined}>
        <DialogTitle>Add a section</DialogTitle>
        <div className="grid gap-3 sm:grid-cols-2">
          {OPTIONS.map(({ type, label, description, Icon }) => (
            <button
              key={type}
              type="button"
              onClick={() => onPick(type)}
              className="flex flex-col items-start gap-1 rounded-md border p-4 text-left transition-colors hover:border-primary hover:bg-muted/50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
            >
              <Icon className="mb-1 h-5 w-5 text-primary" aria-hidden="true" />
              <span className="text-sm font-medium">{label}</span>
              <span className="text-xs text-muted-foreground">{description}</span>
            </button>
          ))}
        </div>
        <p className="text-xs text-muted-foreground">
          The section type can't be changed after it's added.
        </p>
      </DialogContent>
    </Dialog>
  );
}
